/**
 * ShiftRuleDetailPage - Detailansicht/Neuanlage einer Schichtregel (PR-4 + PR-5)
 * 
 * Sektionen:
 * - Grunddaten (Name, Schichttyp)
 * - Zeitfenster (Wochentage, Start, Ende)
 * - Gültigkeit (validFrom, validUntil, Priorität)
 * - PR-5: Overlap-Warnung
 * - Gefahrenzone (Deaktivieren)
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Calendar, ArrowLeft, RefreshCw, Save, Trash2, AlertTriangle, Copy, ChevronDown, ChevronRight, Info, Shield, Layers, Zap, Settings2, Trophy, CircleSlash } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';
import { formatBerlinDateISO } from '../../utils/berlinDate';
import {
  getRuleRole,
  formatRoleLabel,
  getPriorityForRole,
  getWinningPriority,
  compareRulePriority,
  explainWinner,
  formatValidity,
  BASE_PRIORITY,
  OVERRIDE_PRIORITY,
  RuleRole,
} from '../../utils/shiftRuleRole';

interface ShiftType {
  id: string;
  code: string;
  name: string;
}

interface ShiftRule {
  id: string;
  code: string;
  name: string;
  shiftType: ShiftType;
  shiftTypeId: string;
  weekdays: number[];
  startTime: string;
  endTime: string;
  validFrom: string | null;
  validUntil: string | null;
  priority: number;
  isActive: boolean;
}

interface FormData {
  name: string;
  shiftTypeId: string;
  weekdays: number[];
  startTime: string;
  endTime: string;
  validFrom: string;
  validUntil: string;
  priority: number;
}

const INITIAL_FORM: FormData = {
  name: '',
  shiftTypeId: '',
  weekdays: [],
  startTime: '04:00',
  endTime: '14:45',
  validFrom: '',
  validUntil: '',
  priority: 0,
};

// Generate internal code from name (not shown in UI)
const generateCodeFromName = (name: string, existingCodes: string[]): string => {
  // Normalize: uppercase, ASCII, replace spaces/special chars with underscore
  let base = name
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .replace(/[^A-Z0-9]/g, '_') // Replace non-alphanumeric with underscore
    .replace(/_+/g, '_') // Collapse multiple underscores
    .replace(/^_|_$/g, ''); // Trim leading/trailing underscores
  
  if (!base) base = 'RULE';
  
  // Check for collision and add suffix if needed
  if (!existingCodes.includes(base)) return base;
  
  let suffix = 2;
  while (existingCodes.includes(`${base}_${suffix}`)) {
    suffix++;
  }
  return `${base}_${suffix}`;
};

const WEEKDAYS = [
  { value: 1, label: 'Montag', short: 'Mo' },
  { value: 2, label: 'Dienstag', short: 'Di' },
  { value: 3, label: 'Mittwoch', short: 'Mi' },
  { value: 4, label: 'Donnerstag', short: 'Do' },
  { value: 5, label: 'Freitag', short: 'Fr' },
  { value: 6, label: 'Samstag', short: 'Sa' },
  { value: 7, label: 'Sonntag', short: 'So' },
];

export default function ShiftRuleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'new';
  
  const [rule, setRule] = useState<ShiftRule | null>(null);
  const [shiftTypes, setShiftTypes] = useState<ShiftType[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [techOpen, setTechOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Role-Abstraktion: Experten-Modus für manuelle Priority-Eingabe
  const [expertMode, setExpertMode] = useState(false);
  
  // Computed: Aktuelle Rolle basierend auf Formularwerten
  const currentRole: RuleRole = useMemo(() => {
    return getRuleRole({
      priority: formData.priority,
      validFrom: formData.validFrom || null,
      validUntil: formData.validUntil || null,
    });
  }, [formData.priority, formData.validFrom, formData.validUntil]);
  
  // Handler: Rolle ändern
  const handleRoleChange = (role: RuleRole) => {
    const newPriority = getPriorityForRole(role);
    setFormData({ ...formData, priority: newPriority });
  };
  
  // Handler: Auto-Fix Priority für Konflikte
  const handleAutoFixPriority = () => {
    const conflictPriorities = overlappingRules.map(({ rule }) => rule.priority);
    const winningPriority = getWinningPriority(conflictPriorities);
    setFormData({ ...formData, priority: winningPriority });
  };

  // PR-5: All rules for overlap detection
  const [allRules, setAllRules] = useState<ShiftRule[]>([]);

  // Computed: is overnight?
  const isOvernight = formData.endTime && formData.startTime && formData.endTime < formData.startTime;

  // Computed: is full day? (00:00-23:59)
  const isFullDay = formData.startTime === '00:00' && formData.endTime === '23:59';

  // Handler: toggle full day
  const handleFullDayToggle = (checked: boolean) => {
    if (checked) {
      setFormData({ ...formData, startTime: '00:00', endTime: '23:59' });
    } else {
      setFormData({ ...formData, startTime: '04:00', endTime: '14:45' });
    }
  };

  // PR-5: Overlap detection
  const overlappingRules = useMemo(() => {
    if (formData.weekdays.length === 0 || !formData.startTime || !formData.endTime) {
      return [];
    }

    const otherRules = allRules.filter(r => r.id !== id && r.isActive);
    const overlaps: { rule: ShiftRule; reasons: string[] }[] = [];

    for (const other of otherRules) {
      const reasons: string[] = [];

      // 1. Check weekday overlap
      const weekdayOverlap = formData.weekdays.some(d => other.weekdays.includes(d));
      if (!weekdayOverlap) continue;

      // 2. Check validity overlap
      const formValidFrom = formData.validFrom ? new Date(formData.validFrom) : null;
      const formValidUntil = formData.validUntil ? new Date(formData.validUntil) : null;
      const otherValidFrom = other.validFrom ? new Date(other.validFrom) : null;
      const otherValidUntil = other.validUntil ? new Date(other.validUntil) : null;

      // If both have validity ranges, check if they overlap
      const validityOverlap = (() => {
        // If either has no constraints, they potentially overlap
        if (!formValidFrom && !formValidUntil) return true;
        if (!otherValidFrom && !otherValidUntil) return true;
        
        // Check actual overlap
        const formStart = formValidFrom?.getTime() ?? 0;
        const formEnd = formValidUntil?.getTime() ?? Infinity;
        const otherStart = otherValidFrom?.getTime() ?? 0;
        const otherEnd = otherValidUntil?.getTime() ?? Infinity;
        
        return formStart <= otherEnd && formEnd >= otherStart;
      })();

      if (!validityOverlap) continue;

      // 3. Check time window overlap (including overnight handling)
      const timeOverlap = (() => {
        const parseTime = (t: string) => {
          const [h, m] = t.split(':').map(Number);
          return h * 60 + m;
        };

        const formStart = parseTime(formData.startTime);
        const formEnd = parseTime(formData.endTime);
        const otherStart = parseTime(other.startTime);
        const otherEnd = parseTime(other.endTime);

        const formIsOvernight = formEnd < formStart;
        const otherIsOvernight = otherEnd < otherStart;

        // Normalize to ranges for comparison
        const formRanges = formIsOvernight
          ? [[formStart, 1440], [0, formEnd]]
          : [[formStart, formEnd]];
        const otherRanges = otherIsOvernight
          ? [[otherStart, 1440], [0, otherEnd]]
          : [[otherStart, otherEnd]];

        // Check if any ranges overlap
        for (const [fs, fe] of formRanges) {
          for (const [os, oe] of otherRanges) {
            if (fs < oe && fe > os) return true;
          }
        }
        return false;
      })();

      if (!timeOverlap) continue;

      // Build reason list
      const sharedDays = formData.weekdays.filter(d => other.weekdays.includes(d));
      const dayLabels: Record<number, string> = { 1: 'Mo', 2: 'Di', 3: 'Mi', 4: 'Do', 5: 'Fr', 6: 'Sa', 7: 'So' };
      reasons.push(`Gemeinsame Tage: ${sharedDays.map(d => dayLabels[d]).join(', ')}`);
      reasons.push(`Zeitfenster überschneidet sich`);

      overlaps.push({ rule: other, reasons });
    }

    return overlaps;
  }, [formData.weekdays, formData.startTime, formData.endTime, formData.validFrom, formData.validUntil, allRules, id]);

  const loadShiftTypes = async () => {
    try {
      const res = await fetch('/api/admin/shift-types', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setShiftTypes(data.filter((t: ShiftType & { isActive: boolean }) => t.isActive));
      }
    } catch (err) {
      console.error('Fehler beim Laden der Schichttypen:', err);
    }
  };

  // PR-5: Load all rules for overlap detection
  const loadAllRules = async () => {
    try {
      const res = await fetch('/api/admin/shift-rules', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAllRules(data);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Regeln für Overlap-Check:', err);
    }
  };

  const loadRule = async () => {
    if (isNew || !id) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/shift-rules/${id}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setRule(data);
        setFormData({
          name: data.name,
          shiftTypeId: data.shiftType?.id || '',
          weekdays: data.weekdays || [],
          startTime: data.startTime,
          endTime: data.endTime,
          validFrom: data.validFrom ? formatBerlinDateISO(data.validFrom) : '',
          validUntil: data.validUntil ? formatBerlinDateISO(data.validUntil) : '',
          priority: data.priority,
        });
      } else if (res.status === 404) {
        navigate('/admin/shift-rules');
      }
    } catch (err) {
      console.error('Fehler beim Laden der Schichtregel:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadShiftTypes();
    loadAllRules(); // PR-5: Load all rules for overlap detection
    if (!isNew) {
      loadRule();
    }
  }, [id, isNew]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const toggleWeekday = (day: number) => {
    const newWeekdays = formData.weekdays.includes(day)
      ? formData.weekdays.filter(d => d !== day)
      : [...formData.weekdays, day].sort((a, b) => a - b);
    setFormData({ ...formData, weekdays: newWeekdays });
  };

  const handleSave = async () => {
    // Validation
    if (!formData.name.trim()) {
      setError('Name ist erforderlich');
      return;
    }
    if (!formData.shiftTypeId) {
      setError('Schichttyp ist erforderlich');
      return;
    }
    if (formData.weekdays.length === 0) {
      setError('Mindestens ein Wochentag muss ausgewählt sein');
      return;
    }
    if (!formData.startTime || !formData.endTime) {
      setError('Start- und Endzeit sind erforderlich');
      return;
    }
    // Validate time format
    const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;
    if (!timeRegex.test(formData.startTime) || !timeRegex.test(formData.endTime)) {
      setError('Zeitformat muss HH:MM sein');
      return;
    }
    // Validate start != end
    if (formData.startTime === formData.endTime) {
      setError('Start und Ende dürfen nicht identisch sein. Für ganztägig bitte 00:00–23:59 wählen.');
      return;
    }
    // Validate validUntil >= validFrom
    if (formData.validFrom && formData.validUntil && formData.validUntil < formData.validFrom) {
      setError('Gültig bis muss nach Gültig ab liegen');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const url = isNew ? '/api/admin/shift-rules' : `/api/admin/shift-rules/${id}`;
      const body: any = {
        name: formData.name.trim(),
        shiftTypeId: formData.shiftTypeId,
        weekdays: formData.weekdays,
        startTime: formData.startTime,
        endTime: formData.endTime,
        validFrom: formData.validFrom || null,
        validUntil: formData.validUntil || null,
        priority: formData.priority,
      };
      
      if (isNew) {
        // Auto-generate code from name (not shown in UI)
        const existingCodes = allRules.map(r => r.code);
        body.code = generateCodeFromName(formData.name.trim(), existingCodes);
      }
      
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (res.ok) {
        const data = await res.json();
        const savedId = isNew ? data.id : id;
        // Direkt zur Schichten-Übersicht mit Focus auf Regeln-Sektion
        navigate(`/admin/shifts?focus=rules&id=${savedId}`, { replace: true });
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || data.message || 'Fehler beim Speichern');
      }
    } catch (err) {
      setError('Netzwerkfehler');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!id || isNew) return;
    
    setDeleting(true);
    try {
      const res = await fetch(`/api/admin/shift-rules/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (res.ok) {
        navigate('/admin/shift-rules');
      }
    } catch (err) {
      console.error('Fehler beim Deaktivieren:', err);
    }
    setDeleting(false);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-slate-500">Laden...</div>
      </AdminLayout>
    );
  }

  if (!isNew && !rule) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-slate-500">Schichtregel nicht gefunden</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <Link 
              to="/admin/shifts" 
              className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Zurück zu Schichten
            </Link>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Calendar className="w-6 h-6" />
              {isNew ? 'Neue Schichtregel' : rule?.name}
            </h1>
            {!isNew && rule && !rule.isActive && (
              <p className="text-sm text-red-500 mt-1">(inaktiv)</p>
            )}
          </div>
          <div className="flex gap-2">
            {!isNew && (
              <button
                onClick={() => loadRule()}
                disabled={loading}
                className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                title="Aktualisieren"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Speichern...' : 'Speichern'}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* Form Sections */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Grunddaten */}
          <section className="bg-white rounded-lg border p-6 space-y-4">
            <h2 className="font-semibold text-slate-900 border-b pb-2">Grunddaten</h2>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="z.B. Nachtschicht Mo-Fr"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Schichttyp <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.shiftTypeId}
                onChange={(e) => setFormData({ ...formData, shiftTypeId: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Bitte wählen...</option>
                {shiftTypes.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          </section>

          {/* Zeitfenster */}
          <section className="bg-white rounded-lg border p-6 space-y-4">
            <h2 className="font-semibold text-slate-900 border-b pb-2">Zeitfenster</h2>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Wochentage <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {WEEKDAYS.map(day => (
                  <button
                    key={day.value}
                    type="button"
                    onClick={() => toggleWeekday(day.value)}
                    className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      formData.weekdays.includes(day.value)
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    {day.short}
                  </button>
                ))}
              </div>
            </div>
            
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isFullDay}
                onChange={(e) => handleFullDayToggle(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-slate-700">Ganztägig (00:00–23:59)</span>
            </label>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Startzeit <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  value={formData.startTime}
                  onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                  disabled={isFullDay}
                  className={`w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 ${isFullDay ? 'bg-slate-100 text-slate-500' : ''}`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Endzeit <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  value={formData.endTime}
                  onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                  disabled={isFullDay}
                  className={`w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 ${isFullDay ? 'bg-slate-100 text-slate-500' : ''}`}
                />
              </div>
            </div>

            {isOvernight && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <strong>Overnight-Schicht erkannt:</strong> Die Endzeit liegt vor der Startzeit.
                  Das Schichtdatum (shiftDate) ist immer der Tag, an dem die Schicht beginnt.
                </div>
              </div>
            )}
          </section>

          {/* Gültigkeit & Rolle */}
          <section className="bg-white rounded-lg border p-6 space-y-4">
            <h2 className="font-semibold text-slate-900 border-b pb-2">Gültigkeit & Rolle</h2>
            
            {/* Regelrolle (Basis / Ausnahme) */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Regelrolle</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handleRoleChange('base')}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                    currentRole === 'base'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                  }`}
                >
                  <Layers className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-medium">Basis</div>
                    <div className="text-xs opacity-75">Standardregel</div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => handleRoleChange('override')}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                    currentRole === 'override'
                      ? 'border-amber-500 bg-amber-50 text-amber-700'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                  }`}
                >
                  <Shield className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-medium">Ausnahme</div>
                    <div className="text-xs opacity-75">Überschreibt bei Konflikt</div>
                  </div>
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                {currentRole === 'base' 
                  ? 'Basisregeln gelten, wenn keine Ausnahme aktiv ist.'
                  : 'Ausnahmen gewinnen bei Überschneidung gegen Basisregeln.'}
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Gültig ab</label>
                <input
                  type="date"
                  value={formData.validFrom}
                  onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-slate-500 mt-1">Leer = sofort gültig · Tag zählt ab 00:00 mit</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Gültig bis</label>
                <input
                  type="date"
                  value={formData.validUntil}
                  onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-slate-500 mt-1">Leer = unbegrenzt · der Tag zählt <strong>einschließlich</strong> bis 24:00</p>
              </div>
            </div>
            
            {/* Experten-Modus für Priorität */}
            <div className="border-t pt-4">
              <button
                type="button"
                onClick={() => setExpertMode(!expertMode)}
                className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700"
              >
                <Settings2 className="w-4 h-4" />
                Priorität (Experte)
                {expertMode ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
              
              {expertMode && (
                <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Priorität (numerisch)</label>
                  <input
                    type="number"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-slate-500 mt-2">
                    <Info className="w-3 h-3 inline mr-1" />
                    Höhere Zahl gewinnt. Bei Gleichstand entscheidet der Code alphabetisch.
                    <br />
                    <span className="font-medium">Basis = {BASE_PRIORITY}, Ausnahme = {OVERRIDE_PRIORITY}</span>
                  </p>
                </div>
              )}
            </div>
          </section>

          {/* PR-5: Overlap Warning - Verbessert mit Gewinner-Anzeige */}
          {overlappingRules.length > 0 && (
            <section className="bg-amber-50 rounded-lg border border-amber-300 p-4 lg:col-span-2">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-amber-800 mb-2">
                    Überschneidung mit {overlappingRules.length} Regel{overlappingRules.length > 1 ? 'n' : ''}
                  </h3>
                  
                  <ul className="space-y-3">
                    {overlappingRules.map(({ rule: overlap, reasons }) => {
                      const overlapRole = getRuleRole(overlap);
                      const thisWins = formData.priority > overlap.priority || 
                        (formData.priority === overlap.priority && (rule?.code || 'ZZZZ') < overlap.code);
                      const isTie = formData.priority === overlap.priority && rule?.code === overlap.code;
                      
                      const isPriorityTie = formData.priority === overlap.priority;
                      const thisCode = rule?.code || 'ZZZZ';
                      
                      const winnerExplanation = thisWins
                        ? isPriorityTie
                          ? `Gleichstand (Prio ${formData.priority}), Code "${thisCode}" < "${overlap.code}"`
                          : currentRole === 'override' && overlapRole === 'base'
                            ? 'Ausnahme überschreibt Basis'
                            : `Höhere Priorität (${formData.priority} > ${overlap.priority})`
                        : isPriorityTie
                          ? `Gleichstand (Prio ${overlap.priority}), Code "${overlap.code}" < "${thisCode}"`
                          : overlapRole === 'override' && currentRole === 'base'
                            ? 'Ausnahme überschreibt Basis'
                            : `Höhere Priorität (${overlap.priority} > ${formData.priority})`;
                      
                      return (
                        <li key={overlap.id} className="bg-white rounded-lg p-3 border border-amber-200 shadow-sm">
                          {/* Überschrift mit Rolle */}
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-slate-900">{overlap.name}</span>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                overlapRole === 'override' 
                                  ? 'bg-amber-100 text-amber-700' 
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                {overlapRole === 'override' ? 'Ausnahme' : 'Basis'}
                              </span>
                            </div>
                            <span className="text-xs text-slate-500">Prio: {overlap.priority}</span>
                          </div>
                          
                          {/* Schnittmenge */}
                          <div className="text-xs text-slate-600 mb-2 space-y-0.5">
                            {reasons.map((reason, i) => (
                              <div key={i}>{reason}</div>
                            ))}
                            <div>Gültigkeit: {formatValidity(overlap.validFrom, overlap.validUntil)}</div>
                          </div>
                          
                          {/* Gewinner-Box */}
                          <div className={`flex items-center gap-2 p-2 rounded ${
                            thisWins 
                              ? 'bg-green-50 border border-green-200' 
                              : 'bg-red-50 border border-red-200'
                          }`}>
                            {thisWins ? (
                              <>
                                <Trophy className="w-4 h-4 text-green-600" />
                                <span className="text-sm text-green-700">
                                  <strong>Diese Regel gewinnt</strong> ({winnerExplanation})
                                </span>
                              </>
                            ) : (
                              <>
                                <CircleSlash className="w-4 h-4 text-red-600" />
                                <span className="text-sm text-red-700">
                                  <strong>"{overlap.name}" gewinnt</strong> ({winnerExplanation})
                                </span>
                              </>
                            )}
                          </div>
                          
                          {/* Warnung wenn diese Regel verliert */}
                          {!thisWins && (
                            <div className="mt-2 text-xs text-red-600 flex items-start gap-1">
                              <Info className="w-3 h-3 mt-0.5 shrink-0" />
                              Diese Regel wird im Schnittbereich NICHT aktiv sein.
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                  
                  {/* Auto-Fix Button */}
                  {overlappingRules.some(({ rule }) => formData.priority <= rule.priority) && (
                    <div className="mt-4 flex items-center gap-3">
                      <button
                        type="button"
                        onClick={handleAutoFixPriority}
                        className="px-3 py-1.5 bg-amber-600 text-white text-sm rounded-lg hover:bg-amber-700 flex items-center gap-1.5"
                      >
                        <Zap className="w-4 h-4" />
                        Priorität automatisch erhöhen
                      </button>
                      <span className="text-xs text-amber-700">
                        Setzt Priorität auf {getWinningPriority(overlappingRules.map(({ rule }) => rule.priority))}, damit diese Regel gewinnt.
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}

        </div>

        {/* Delete Section (nur bei Edit) */}
        {!isNew && rule && (
          <section className="bg-white rounded-lg border border-red-200 p-6">
            <h2 className="font-semibold text-red-600 mb-2">Gefahrenzone</h2>
            <p className="text-sm text-slate-600 mb-4">
              Das Deaktivieren einer Schichtregel bedeutet, dass während des definierten Zeitfensters 
              keine Erfassung mehr möglich ist (wenn keine andere Regel greift).
              Historische Daten bleiben erhalten.
            </p>
            <button
              onClick={() => setDeleteConfirmOpen(true)}
              className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Schichtregel deaktivieren
            </button>
          </section>
        )}
      </div>

      {/* Delete Confirm Modal */}
      {deleteConfirmOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
            <div className="p-4 border-b flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600" />
              <h2 className="text-lg font-semibold text-red-600">Schichtregel deaktivieren?</h2>
            </div>
            <div className="p-4">
              <p className="text-slate-600">
                <strong>"{rule?.name}"</strong> wird deaktiviert.
              </p>
              <p className="text-sm text-slate-500 mt-2">
                Erfassung während dieses Zeitfensters ist dann nicht mehr möglich.
              </p>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t bg-slate-50 rounded-b-xl">
              <button
                onClick={() => setDeleteConfirmOpen(false)}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-100"
              >
                Abbrechen
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Deaktivieren...' : 'Deaktivieren'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
